import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Environment, EnvironmentService } from '@galaxy/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Pricing } from '../../../common/table-pricing/interface';
import { AppIDs } from '../../../constants';
import { ProductService } from '../../../core/product.service';
import { Product } from '../../../core/product/product';
import { godaddyAppId } from '../../../marketplace-app/global-brands-constants';
import { ActivationService } from '../../core/activation.service';
import { ActivatableItem, ActivatableItemCount } from '../../core/interface';

export const APP_NAMES = {
  WEBSITE: 'website',
  GO_DADDY: 'go_daddy',
};

export const PRO_EDITION = 'Pro';

@Component({
  selector: 'app-custom-product-recommendations',
  templateUrl: './custom-product-recommendations.component.html',
  styleUrls: ['./custom-product-recommendations.component.scss'],
  standalone: false,
})
export class CustomProductRecommendationsComponent implements OnInit, OnDestroy {
  @Input() appCartItems$: Observable<ActivatableItemCount[]>;
  appNames = APP_NAMES;

  // the current custom recommendation is in place for WSP and GoDaddy
  goDaddy$: Observable<Product>;
  // only the pro version of Website is used here as the Express version does not allow setting custom domains
  websitePro$: Observable<Product>;
  checkedGoDaddy = false;
  checkedWebsitePro = false;
  goDaddyPricing: Pricing;
  websiteProPricing: Pricing;

  private subscriptions: Subscription[] = [];

  constructor(
    private activationService: ActivationService,
    private environmentService: EnvironmentService,
    private productService: ProductService,
  ) {
    this.setProducts(environmentService.getEnvironment());
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.appCartItems$
        .pipe(
          map((aics: ActivatableItemCount[]) => {
            let foundWebsitePro = false;
            let foundGoDaddy = false;
            aics.forEach((aic: ActivatableItemCount) => {
              switch (aic.activatableItem.appId) {
                case AppIDs.WebsiteDemo:
                case AppIDs.Website:
                  // cannot add custom domains on Website Express
                  if (aic.editionName.toLowerCase() === PRO_EDITION.toLowerCase()) {
                    foundWebsitePro = true;
                  }
                  break;
                case godaddyAppId[Environment.DEMO]:
                case godaddyAppId[Environment.PROD]:
                  foundGoDaddy = true;
                  break;
              }
            });
            if (foundWebsitePro && foundGoDaddy) {
              this.checkedWebsitePro = false;
              this.checkedGoDaddy = false;
            } else {
              this.checkedWebsitePro = foundWebsitePro;
              this.checkedGoDaddy = foundGoDaddy;
            }
          }),
        )
        .subscribe(),
    );
  }

  /**
   * Sets the product subscriptions based on the environment that is passed in. This only sets the subscriptions for
   * the GoDaddy and WebsitePro products
   * @param env Environment.DEMO || Environment.PROD
   */
  setProducts(env: Environment): void {
    if (env === Environment.PROD) {
      this.setGoDaddyWSP(godaddyAppId[Environment.PROD], AppIDs.Website);
      this.setGoDaddyWSPPricing(godaddyAppId[Environment.PROD], AppIDs.Website);
    } else {
      this.setGoDaddyWSP(godaddyAppId[Environment.DEMO], AppIDs.WebsiteDemo);
      this.setGoDaddyWSPPricing(godaddyAppId[Environment.DEMO], AppIDs.WebsiteDemo);
    }
  }

  /**
   * Sets the subscriptions for obtaining GoDaddy and WebsitePro app details
   * @param goDaddyAppId the app ID of GoDaddy, according to the environment
   * @param wspAppId the app ID of WebsitePro, according to the environment
   */
  setGoDaddyWSP(goDaddyAppId: string, wspAppId: string): void {
    this.goDaddy$ = this.productService.appWithId(goDaddyAppId);
    this.websitePro$ = this.productService.appWithId(wspAppId);
  }

  /**
   * Finds the pricing of GoDaddy and WebsitePro based on the app ID passed in according to the environment
   * specification
   * @param goDaddyAppId the app ID of GoDaddy, according to the environment
   * @param wspAppId the app ID of WebsitePro, according to the environment
   */
  setGoDaddyWSPPricing(goDaddyAppId: string, wspAppId: string): void {
    this.subscriptions.push(
      this.activationService.activatableItems$.subscribe((items: ActivatableItem[]) => {
        this.goDaddyPricing = items.find((item: ActivatableItem) => item.appId === goDaddyAppId)?.pricing;
        this.websiteProPricing = items.find((item: ActivatableItem) => item.appId === wspAppId)?.pricing;
      }),
    );
  }

  /**
   * Selects the appropriate product ID based on the product name that is passed in and adds the item to the cart
   * @param product APP_NAMES.WEBSITE || APP_NAMES.GO_DADDY
   */
  addProduct(product: string): void {
    let appId = '';
    switch (product) {
      case APP_NAMES.WEBSITE:
        appId = godaddyAppId[this.environmentService.getEnvironment()];
        break;
      case APP_NAMES.GO_DADDY:
        appId = this.environmentService.getEnvironment() === Environment.DEMO ? AppIDs.WebsiteDemo : AppIDs.Website;
        break;
    }
    this.activationService.selectItem({ appId: appId, quantity: 1 });
  }

  /**
   * Removes WebsitePro and GoDaddy from the cart
   */
  removeCheckedProducts(): void {
    this.checkedWebsitePro = false;
    this.checkedGoDaddy = false;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
